import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { ArrowLeft, Camera, Upload, Plus, Sparkles, FileText, Trash2, ShieldCheck, Users as UsersIcon, Phone, IdCard, ClipboardCheck, Heart, ChevronRight, FileSignature, FilePen, ClipboardList, MessageSquare, FileBarChart, AlertTriangle, Cross, X, Printer, Activity } from "lucide-react";
import { toast } from "sonner";
import PatientDocuments from "./PatientDocuments";
import { getDocType } from "../lib/docTypes";
import { printPatientBasic } from "../lib/printDoc";
import GafTrendChart from "../components/GafTrendChart";

const docCount = (docs, category) =>
  (docs || []).filter((d) => getDocType(d.doc_type)?.category === category).length;

const INS_KINDS = {
  care_insurance: "介護保険被保険者証",
  medical_insurance: "医療保険証",
  self_support: "自立支援医療受給者証",
  public_fund: "公費負担医療受給者証",
  disability_certificate: "障害者手帳",
};

const INS_KIND_LIST = Object.entries(INS_KINDS).map(([v, l]) => ({ value: v, label: l }));

const INST_KINDS = {
  general: "一般 訪問看護指示書",
  psychiatric: "精神科 訪問看護指示書",
  special: "特別訪問看護指示書",
  iv: "在宅患者訪問点滴注射指示書",
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function PatientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [insurances, setInsurances] = useState([]);
  const [instructions, setInstructions] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [activeCard, setActiveCard] = useState(null);
  const [org, setOrg] = useState({});

  const load = async () => {
    const r = await api.get(`/patients/${id}`);
    setP(r.data);
    setForm(r.data);
    const [ins, inst, docs, refs] = await Promise.all([
      api.get("/insurances", { params: { patient_id: id } }),
      api.get("/instructions", { params: { patient_id: id } }),
      api.get("/documents", { params: { patient_id: id } }),
      api.get("/referrers"),
    ]);
    setInsurances(ins.data);
    setInstructions(inst.data);
    setDocuments(docs.data);
    setReferrers(refs.data);
  };

  // Load organization (for print footer)
  useEffect(() => {
    api.get("/organization").then((r) => setOrg(r.data)).catch(() => {});
  }, []);

  // Filter referrers to medical institutions/hospitals/clinics
  const medicalReferrers = referrers.filter((r) =>
    ["医療機関", "病院", "クリニック"].includes(r.kind)
  );

  const applyReferrer = (refId) => {
    const r = referrers.find((x) => x.id === refId);
    if (!r) return;
    setForm({
      ...form,
      primary_medical_institution: r.name,
      primary_doctor_name: r.person_in_charge || form.primary_doctor_name || "",
      doctor_phone: r.phone || form.doctor_phone || "",
    });
    toast.success(`${r.name} を医療機関情報に適用しました`);
  };

  useEffect(() => {
    load();
  }, [id]);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const b64 = await fileToBase64(file);
    await api.patch(`/patients/${id}`, { photo_base64: b64 });
    toast.success("写真を更新しました");
    load();
  };

  const save = async () => {
    const { id: _id, tenant_id: _t, _id: __, ...rest } = form;
    await api.patch(`/patients/${id}`, rest);
    toast.success("保存しました");
    setEditing(false);
    load();
  };

  if (!p) return <div className="text-center py-12 text-[#8A8A86]">読み込み中...</div>;

  return (
    <div className="space-y-6" data-testid="patient-detail-page">
      <button onClick={() => nav("/patients")} className="flex items-center gap-2 text-sm text-[#4A4A48] hover:text-[#2C5545]">
        <ArrowLeft size={16} /> 利用者一覧に戻る
      </button>

      <div className="flex items-start gap-6 flex-wrap">
        <div className="relative">
          <div className="w-24 h-24 rounded-2xl bg-[#D9E2D5] overflow-hidden flex items-center justify-center">
            {p.photo_base64 ? (
              <img src={p.photo_base64} alt="" className="w-full h-full object-cover" />
            ) : (
              <UsersIcon size={36} className="text-[#2C5545]" />
            )}
          </div>
          <label className="absolute -bottom-2 -right-2 bg-white border border-[#E5E5E3] rounded-full p-2 cursor-pointer hover:bg-[#F3F3F1]">
            <Camera size={14} className="text-[#2C5545]" />
            <input data-testid="photo-upload-input" type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
          </label>
        </div>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">{p.name_kanji}</h1>
          <p className="text-[#8A8A86] font-numeric">
            {p.name_kana} {p.birth_date && `· ${p.birth_date}`} {p.gender && `· ${p.gender}`}
          </p>
          <div className="flex gap-2 mt-3 flex-wrap">
            {p.care_level && <Badge variant="outline" className="border-[#D9E2D5] text-[#2C5545]">{p.care_level}</Badge>}
            {p.main_diagnosis && <Badge variant="outline" className="border-[#E5E5E3]">{p.main_diagnosis}</Badge>}
          </div>
        </div>
      </div>

      {(() => {
        const CARDS = [
          { val: "basic", testid: "tab-basic", label: "基本情報", desc: "氏名・病名・主治医", icon: IdCard, count: null, color: "#2C5545" },
          { val: "family", testid: "tab-family", label: "家族・連絡先", desc: "家族情報・緊急連絡先", icon: Heart, count: (p.family_members?.length || 0) + (p.emergency_contacts?.length || 0), color: "#A74141" },
          { val: "insurance", testid: "tab-insurance", label: "保険・手帳", desc: "介護/医療/手帳・履歴", icon: ShieldCheck, count: insurances.length, color: "#4A6B8C" },
          { val: "instruction", testid: "tab-instruction", label: "指示書", desc: "訪問看護指示書・OCR", icon: ClipboardCheck, count: instructions.length, color: "#D4A373" },
          { val: "gaf_trend", testid: "tab-gaf-trend", label: "GAF 推移", desc: "看護記録のGAFスコア推移グラフ", icon: Activity, count: null, color: "#3A6B52" },
          { val: "doc_application", testid: "tab-doc-application", label: "申込・契約", desc: "利用申込・重要事項・契約書", icon: FileSignature, count: docCount(documents, "申込・契約"), color: "#3A6B52" },
          { val: "doc_consent", testid: "tab-doc-consent", label: "同意書", desc: "個人情報・緊急時・医療共有", icon: FilePen, count: docCount(documents, "同意書"), color: "#2C5545" },
          { val: "doc_plan", testid: "tab-doc-plan", label: "計画・アセスメント", desc: "計画書・アセスメント", icon: ClipboardList, count: docCount(documents, "計画・アセスメント"), color: "#4A6B8C" },
          { val: "doc_meeting", testid: "tab-doc-meeting", label: "会議・連携", desc: "担当者会議・多職種連携", icon: MessageSquare, count: docCount(documents, "会議・連携"), color: "#8B6424" },
          { val: "doc_report", testid: "tab-doc-report", label: "記録・報告", desc: "報告書・モニタリング・意見書", icon: FileBarChart, count: docCount(documents, "記録・報告"), color: "#6B5B4D" },
          { val: "doc_risk", testid: "tab-doc-risk", label: "緊急・リスク", desc: "事故・苦情・緊急対応計画", icon: AlertTriangle, count: docCount(documents, "緊急・リスク"), color: "#A74141" },
          { val: "doc_eol", testid: "tab-doc-eol", label: "看取り・終了", desc: "看取り同意・死亡時・終了", icon: Cross, count: docCount(documents, "看取り・終了"), color: "#4A4A48" },
        ];
        const active = CARDS.find((c) => c.val === activeCard);
        const renderBody = (val) => {
          if (val === "basic") {
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {editing && medicalReferrers.length > 0 && (
                  <div className="md:col-span-2 p-3 rounded-lg bg-[#D9E2D5]/30 border border-[#D9E2D5]">
                    <Label className="text-xs text-[#2C5545]">医療機関マスタから選択(主治医・医療機関・電話を自動入力)</Label>
                    <select
                      data-testid="referrer-select"
                      onChange={(e) => applyReferrer(e.target.value)}
                      defaultValue=""
                      className="mt-1 flex h-10 w-full rounded-md border border-[#E5E5E3] bg-white px-3 text-sm"
                    >
                      <option value="">医療機関を選択して自動入力...</option>
                      {medicalReferrers.map((r) => (
                        <option key={r.id} value={r.id}>
                          [{r.kind}] {r.name}{r.person_in_charge ? ` / ${r.person_in_charge}` : ""}{r.phone ? ` (${r.phone})` : ""}
                        </option>
                      ))}
                    </select>
                    <div className="text-[10px] text-[#8A8A86] mt-1">
                      医療機関がリストにない場合は「医療機関元」メニューから先に登録してください。
                    </div>
                  </div>
                )}
                {[
                  ["氏名(漢字)", "name_kanji"],
                  ["氏名(カナ)", "name_kana"],
                  ["生年月日", "birth_date", "date"],
                  ["性別", "gender"],
                  ["郵便番号", "postal_code"],
                  ["電話番号", "phone"],
                  ["住所", "address", "text", true],
                  ["主病名", "main_diagnosis"],
                  ["要介護度", "care_level"],
                  ["障害区分", "disability_grade"],
                  ["主治医", "primary_doctor_name"],
                  ["医療機関", "primary_medical_institution"],
                  ["主治医 電話", "doctor_phone"],
                ].map(([l, k, t, full]) => (
                  <div key={k} className={full ? "md:col-span-2" : ""}>
                    <Label>{l}</Label>
                    {editing ? (
                      <Input type={t || "text"} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                    ) : (
                      <div className="mt-2 text-[#1C1C1A]">{p[k] || <span className="text-[#8A8A86]">—</span>}</div>
                    )}
                  </div>
                ))}

                {/* 契約状況 */}
                <div>
                  <Label>契約状況</Label>
                  {editing ? (
                    <select
                      data-testid="contract-status-select"
                      value={form.contract_status || ""}
                      onChange={(e) => setForm({ ...form, contract_status: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input px-3 text-sm bg-white"
                    >
                      <option value="">選択してください</option>
                      {["契約中", "契約予定", "休止中", "一時中断", "入院中", "契約終了"].map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                      {form.contract_status && !["契約中", "契約予定", "休止中", "一時中断", "入院中", "契約終了"].includes(form.contract_status) && (
                        <option value={form.contract_status}>{form.contract_status} (登録外)</option>
                      )}
                    </select>
                  ) : (
                    <div className="mt-2">
                      {p.contract_status ? (
                        <span
                          data-testid="contract-status-badge"
                          className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            p.contract_status === "契約中" ? "bg-[#2C5545] text-white" :
                            p.contract_status === "契約予定" ? "bg-[#4A6B8C] text-white" :
                            p.contract_status === "休止中" || p.contract_status === "一時中断" ? "bg-[#D4A050] text-white" :
                            p.contract_status === "入院中" ? "bg-[#A74141] text-white" :
                            p.contract_status === "契約終了" ? "bg-[#8A8A86] text-white" :
                            "bg-[#E5E5E3] text-[#1C1C1A]"
                          }`}
                        >
                          {p.contract_status}
                        </span>
                      ) : (
                        <span className="text-[#8A8A86]">—</span>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <Label>契約開始日</Label>
                  {editing ? (
                    <Input
                      data-testid="contract-start-input"
                      type="date"
                      value={form.contract_start_date || ""}
                      onChange={(e) => setForm({ ...form, contract_start_date: e.target.value })}
                    />
                  ) : (
                    <div className="mt-2 text-[#1C1C1A]">{p.contract_start_date || <span className="text-[#8A8A86]">—</span>}</div>
                  )}
                </div>
                <div>
                  <Label>契約終了日</Label>
                  {editing ? (
                    <Input
                      data-testid="contract-end-input"
                      type="date"
                      value={form.contract_end_date || ""}
                      onChange={(e) => setForm({ ...form, contract_end_date: e.target.value })}
                    />
                  ) : (
                    <div className="mt-2 text-[#1C1C1A]">{p.contract_end_date || <span className="text-[#8A8A86]">—</span>}</div>
                  )}
                </div>
                {[
                  ["ADL", "adl"],
                  ["アレルギー", "allergies"],
                  ["服薬情報", "medications"],
                  ["既往歴", "medical_history"],
                ].map(([l, k]) => (
                  <div key={k} className="md:col-span-2">
                    <Label>{l}</Label>
                    {editing ? (
                      <Textarea value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                    ) : (
                      <div className="mt-2 text-[#1C1C1A] whitespace-pre-wrap">{p[k] || <span className="text-[#8A8A86]">—</span>}</div>
                    )}
                  </div>
                ))}

                {/* 他の医療機関 (複数登録) */}
                <div className="md:col-span-2">
                  <Label>他の医療機関(複数登録可)</Label>
                  {editing ? (
                    <div className="mt-1 space-y-2">
                      {(form.additional_medical_institutions || []).length > 0 && (
                        <div className="flex flex-wrap gap-2" data-testid="detail-additional-list">
                          {(form.additional_medical_institutions || []).map((inst, i) => (
                            <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D9E2D5]/40 border border-[#2C5545]/30 text-sm">
                              <div>
                                <div className="font-medium">{inst.name}</div>
                                {(inst.doctor_name || inst.phone) && (
                                  <div className="text-[10px] text-[#4A4A48]">
                                    {inst.doctor_name || ""}{inst.doctor_name && inst.phone ? " / " : ""}{inst.phone || ""}
                                  </div>
                                )}
                              </div>
                              <button
                                type="button"
                                data-testid={`detail-remove-additional-${i}`}
                                onClick={() => {
                                  const next = [...(form.additional_medical_institutions || [])];
                                  next.splice(i, 1);
                                  setForm({ ...form, additional_medical_institutions: next });
                                }}
                                className="text-[#A74141] hover:bg-[#A74141]/10 rounded-full w-5 h-5 flex items-center justify-center"
                                title="削除"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <select
                        data-testid="detail-add-additional-select"
                        value=""
                        onChange={(e) => {
                          const refId = e.target.value;
                          if (!refId) return;
                          const r = referrers.find((x) => x.id === refId);
                          if (!r) return;
                          const list = form.additional_medical_institutions || [];
                          if (list.some((x) => x.name === r.name) || form.primary_medical_institution === r.name) {
                            toast.error(`${r.name} は既に登録されています`);
                            e.target.value = "";
                            return;
                          }
                          setForm({
                            ...form,
                            additional_medical_institutions: [
                              ...list,
                              { name: r.name, doctor_name: r.person_in_charge || "", phone: r.phone || "", kind: r.kind || "" },
                            ],
                          });
                          e.target.value = "";
                        }}
                        className="flex h-10 w-full rounded-md border border-input px-3 text-sm bg-white"
                      >
                        <option value="">+ 他の医療機関を追加(医療機関マスタから選択)</option>
                        {referrers
                          .filter((r) => r.name !== form.primary_medical_institution &&
                                         !(form.additional_medical_institutions || []).some((x) => x.name === r.name))
                          .map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.kind ? `[${r.kind}] ` : ""}{r.name}{r.person_in_charge ? ` / ${r.person_in_charge}` : ""}
                            </option>
                          ))}
                      </select>
                    </div>
                  ) : (
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="detail-additional-readonly">
                      {(p.additional_medical_institutions && p.additional_medical_institutions.length > 0) ? (
                        p.additional_medical_institutions.map((inst, i) => (
                          <div key={i} className="px-3 py-1.5 rounded-full bg-[#D9E2D5]/40 border border-[#2C5545]/30 text-sm">
                            <div className="font-medium">{inst.name}</div>
                            {(inst.doctor_name || inst.phone) && (
                              <div className="text-[10px] text-[#4A4A48]">
                                {inst.doctor_name || ""}{inst.doctor_name && inst.phone ? " / " : ""}{inst.phone || ""}
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <span className="text-[#8A8A86]">—</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          }
          if (val === "family") return <FamilyEditor patient={p} onSaved={load} />;
          if (val === "insurance") return <InsuranceManager patientId={id} items={insurances} onChanged={load} />;
          if (val === "instruction") return <InstructionManager patientId={id} items={instructions} onChanged={load} />;
          if (val === "gaf_trend") return <GafTrendChart patientId={id} />;
          const catMap = {
            doc_application: "申込・契約", doc_consent: "同意書", doc_plan: "計画・アセスメント",
            doc_meeting: "会議・連携", doc_report: "記録・報告", doc_risk: "緊急・リスク", doc_eol: "看取り・終了",
          };
          if (catMap[val]) return <PatientDocuments patient={p} category={catMap[val]} />;
          return null;
        };
        return (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
              {CARDS.map((t) => (
                <button
                  key={t.val}
                  data-testid={t.testid}
                  onClick={() => { if (t.val === "basic") setEditing(false); setActiveCard(t.val); }}
                  className="text-left"
                >
                  <Card className="p-4 w-full border-[#E5E5E3] bg-white hover:shadow-md hover:border-[#2C5545]/40 transition-all h-full">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: t.color + "15" }}>
                        <t.icon size={18} style={{ color: t.color }} strokeWidth={1.5} />
                      </div>
                      {t.count !== null && (
                        <span className="text-[10px] bg-white border border-[#E5E5E3] px-2 py-0.5 rounded-full font-numeric font-medium text-[#2C5545]">
                          {t.count}件
                        </span>
                      )}
                    </div>
                    <div className="font-bold text-[#1C1C1A] text-sm leading-snug">{t.label}</div>
                    <div className="text-[11px] text-[#8A8A86] mt-0.5 leading-tight">{t.desc}</div>
                    <div className="flex items-center gap-1 mt-3 text-[11px] text-[#2C5545] opacity-70">
                      <span className="font-numeric tracking-wider">開く</span>
                      <ChevronRight size={11} />
                    </div>
                  </Card>
                </button>
              ))}
            </div>

            <Dialog open={!!activeCard} onOpenChange={(o) => { if (!o) { setActiveCard(null); setEditing(false); } }}>
              <DialogContent
                data-testid={active ? `dialog-${active.testid}` : undefined}
                className="max-w-5xl max-h-[90vh] overflow-y-auto p-0"
              >
                {active && (
                  <>
                    <div className="sticky top-0 z-10 bg-white border-b border-[#E5E5E3] px-6 py-4 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: active.color + "15" }}>
                          <active.icon size={18} style={{ color: active.color }} strokeWidth={1.5} />
                        </div>
                        <div className="min-w-0">
                          <DialogTitle className="text-lg font-bold truncate">{active.label}</DialogTitle>
                          <div className="text-xs text-[#8A8A86] truncate">{p.name_kanji} さん · {active.desc}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 mr-12">
                        {active.val === "basic" && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              data-testid="print-basic-button"
                              onClick={() => printPatientBasic(p, org)}
                              className="border-[#2C5545] text-[#2C5545]"
                            >
                              <Printer size={14} className="mr-1" />
                              印刷
                            </Button>
                            {!editing ? (
                              <Button variant="outline" data-testid="edit-basic-button" onClick={() => setEditing(true)}>編集</Button>
                            ) : (
                              <>
                                <Button variant="outline" onClick={() => { setEditing(false); setForm(p); }}>キャンセル</Button>
                                <Button className="bg-[#2C5545] text-white" data-testid="save-basic-button" onClick={save}>保存</Button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="px-6 py-6">
                      {renderBody(active.val)}
                    </div>
                  </>
                )}
              </DialogContent>
            </Dialog>
          </>
        );
      })()}
    </div>
  );
}

function FamilyEditor({ patient, onSaved }) {
  const [family, setFamily] = useState(patient.family_members || []);
  const [emergency, setEmergency] = useState(patient.emergency_contacts || []);

  const save = async () => {
    await api.patch(`/patients/${patient.id}`, { family_members: family, emergency_contacts: emergency });
    toast.success("保存しました");
    onSaved();
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 border-[#E5E5E3]">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">家族情報</h3>
          <Button data-testid="add-family-button" variant="outline" onClick={() => setFamily([...family, { relation: "", name: "", phone: "" }])}>
            <Plus size={14} className="mr-1" /> 追加
          </Button>
        </div>
        <div className="space-y-3">
          {family.map((f, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
              <div>
                <Label>続柄</Label>
                <Input value={f.relation} onChange={(e) => { const c = [...family]; c[i].relation = e.target.value; setFamily(c); }} />
              </div>
              <div>
                <Label>氏名</Label>
                <Input value={f.name} onChange={(e) => { const c = [...family]; c[i].name = e.target.value; setFamily(c); }} />
              </div>
              <div>
                <Label>電話</Label>
                <Input value={f.phone} onChange={(e) => { const c = [...family]; c[i].phone = e.target.value; setFamily(c); }} />
              </div>
              <Button variant="outline" onClick={() => setFamily(family.filter((_, j) => j !== i))}>
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
          {family.length === 0 && <div className="text-[#8A8A86] text-center py-4">家族情報が登録されていません</div>}
        </div>
      </Card>

      <Card className="p-6 border-[#E5E5E3]">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">緊急連絡先</h3>
          <Button data-testid="add-emergency-button" variant="outline" onClick={() => setEmergency([...emergency, { priority: emergency.length + 1, name: "", relation: "", phone: "" }])}>
            <Plus size={14} className="mr-1" /> 追加
          </Button>
        </div>
        <div className="space-y-3">
          {emergency.map((f, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
              <div>
                <Label>優先順位</Label>
                <Input type="number" value={f.priority} onChange={(e) => { const c = [...emergency]; c[i].priority = parseInt(e.target.value); setEmergency(c); }} />
              </div>
              <div>
                <Label>氏名</Label>
                <Input value={f.name} onChange={(e) => { const c = [...emergency]; c[i].name = e.target.value; setEmergency(c); }} />
              </div>
              <div>
                <Label>続柄</Label>
                <Input value={f.relation} onChange={(e) => { const c = [...emergency]; c[i].relation = e.target.value; setEmergency(c); }} />
              </div>
              <div>
                <Label>電話</Label>
                <Input value={f.phone} onChange={(e) => { const c = [...emergency]; c[i].phone = e.target.value; setEmergency(c); }} />
              </div>
              <Button variant="outline" onClick={() => setEmergency(emergency.filter((_, j) => j !== i))}>
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
          {emergency.length === 0 && <div className="text-[#8A8A86] text-center py-4">緊急連絡先が登録されていません</div>}
        </div>
      </Card>

      <Button data-testid="save-family-button" className="bg-[#2C5545] text-white" onClick={save}>保存</Button>
    </div>
  );
}

function InsuranceManager({ patientId, items, onChanged }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ kind: "care_insurance", number: "", issue_date: "", expiry_date: "", image_base64: "", note: "" });

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const b64 = await fileToBase64(f);
    setForm({ ...form, image_base64: b64 });
  };

  const save = async () => {
    await api.post("/insurances", { patient_id: patientId, ...form });
    toast.success("保険書類を追加しました");
    setOpen(false);
    setForm({ kind: "care_insurance", number: "", issue_date: "", expiry_date: "", image_base64: "", note: "" });
    onChanged();
  };

  const del = async (id) => {
    if (!confirm("削除しますか?")) return;
    await api.delete(`/insurances/${id}`);
    onChanged();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-[#4A4A48]">保険証・受給者証・手帳(履歴追加可能 / {items.length}件)</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-insurance-button" className="bg-[#2C5545] text-white"><Plus size={14} className="mr-1" /> 書類追加</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>保険書類・手帳 追加</DialogTitle></DialogHeader>
            <div className="space-y-3 py-3">
              <div>
                <Label>種別</Label>
                <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="flex h-10 w-full rounded-md border border-input px-3 text-sm">
                  {INS_KIND_LIST.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div><Label>番号</Label><Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} /></div>
              <div><Label>等級・要介護度等</Label><Input value={form.grade_or_level || ""} onChange={(e) => setForm({ ...form, grade_or_level: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>交付日</Label><Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></div>
                <div><Label>有効期限</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></div>
              </div>
              <div>
                <Label>画像</Label>
                <Input type="file" accept="image/*" onChange={handleFile} data-testid="insurance-image-input" />
                {form.image_base64 && <img src={form.image_base64} className="mt-2 max-h-40 rounded border border-[#E5E5E3]" alt="" />}
              </div>
              <div><Label>備考</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>キャンセル</Button>
              <Button data-testid="save-insurance-button" className="bg-[#2C5545] text-white" onClick={save}>追加</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((it) => (
          <Card key={it.id} className="p-4 border-[#E5E5E3]">
            <div className="flex items-start gap-3">
              <ShieldCheck size={18} className="text-[#2C5545] mt-0.5" />
              <div className="flex-1">
                <div className="font-medium">{INS_KINDS[it.kind]}</div>
                <div className="text-sm text-[#8A8A86] font-numeric">
                  {it.number} {it.grade_or_level && `· ${it.grade_or_level}`}
                </div>
                <div className="text-xs text-[#8A8A86] font-numeric mt-1">
                  {it.issue_date || "-"} 〜 {it.expiry_date || "-"}
                </div>
                {it.image_base64 && <img src={it.image_base64} alt="" className="mt-2 max-h-32 rounded border border-[#E5E5E3]" />}
              </div>
              <button onClick={() => del(it.id)} className="text-[#A74141]"><Trash2 size={14} /></button>
            </div>
          </Card>
        ))}
        {items.length === 0 && <div className="col-span-2 text-center py-8 text-[#8A8A86]">保険書類が登録されていません</div>}
      </div>
    </div>
  );
}

function InstructionManager({ patientId, items, onChanged }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ kind: "general", doctor_name: "", medical_institution: "", start_date: "", end_date: "", diagnosis: "", content: "", image_base64: "", received_date: "" });
  const [ocrLoading, setOcrLoading] = useState(false);

  const createRequestDoc = async (insId) => {
    try {
      const r = await api.post(`/instructions/${insId}/request-document`);
      toast.success(r.data.created ? "指示書依頼書の下書きを作成しました" : "既存の依頼書を開きます");
      nav("/documents");
    } catch (e) {
      toast.error("依頼書の作成に失敗しました: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const b64 = await fileToBase64(f);
    setForm({ ...form, image_base64: b64 });
  };

  const runOCR = async () => {
    if (!form.image_base64) {
      toast.error("先に画像をアップロードしてください");
      return;
    }
    setOcrLoading(true);
    try {
      const r = await api.post("/instructions/ocr", { image_base64: form.image_base64 });
      setForm({
        ...form,
        doctor_name: r.data.doctor_name || form.doctor_name,
        medical_institution: r.data.medical_institution || form.medical_institution,
        start_date: form.start_date,
        end_date: form.end_date,
        diagnosis: r.data.diagnosis || form.diagnosis,
        content: r.data.content || form.content,
        ocr_text: r.data.ocr_text,
      });
      toast.success("OCR読取完了。内容を確認してください");
    } catch (e) {
      toast.error("OCR失敗: " + (e?.response?.data?.detail || e.message));
    } finally {
      setOcrLoading(false);
    }
  };

  const save = async () => {
    await api.post("/instructions", { patient_id: patientId, ...form });
    toast.success("指示書を追加しました");
    setOpen(false);
    setForm({ kind: "general", doctor_name: "", medical_institution: "", start_date: "", end_date: "", diagnosis: "", content: "", image_base64: "", received_date: "" });
    onChanged();
  };

  const del = async (id) => {
    if (!confirm("削除しますか?")) return;
    await api.delete(`/instructions/${id}`);
    onChanged();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-[#4A4A48]">訪問看護指示書(履歴追加可能 / {items.length}件) · OCR自動入力対応</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-instruction-button" className="bg-[#2C5545] text-white"><Plus size={14} className="mr-1" /> 指示書追加</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>訪問看護指示書 追加</DialogTitle></DialogHeader>
            <div className="space-y-3 py-3">
              <div>
                <Label>種別</Label>
                <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="flex h-10 w-full rounded-md border border-input px-3 text-sm">
                  {Object.entries(INST_KINDS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>

              <div>
                <Label>指示書の写真(OCR自動入力可)</Label>
                <Input data-testid="instruction-image-input" type="file" accept="image/*" onChange={handleFile} />
                {form.image_base64 && (
                  <>
                    <img src={form.image_base64} className="mt-2 max-h-60 rounded border border-[#E5E5E3]" alt="" />
                    <Button
                      data-testid="ocr-run-button"
                      type="button"
                      variant="outline"
                      onClick={runOCR}
                      disabled={ocrLoading}
                      className="mt-2 border-[#2C5545] text-[#2C5545]"
                    >
                      <Sparkles size={14} className="mr-1" />
                      {ocrLoading ? "OCR処理中..." : "OCRで自動入力"}
                    </Button>
                  </>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><Label>主治医名</Label><Input value={form.doctor_name} onChange={(e) => setForm({ ...form, doctor_name: e.target.value })} /></div>
                <div><Label>医療機関</Label><Input value={form.medical_institution} onChange={(e) => setForm({ ...form, medical_institution: e.target.value })} /></div>
                <div><Label>指示期間 開始</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                <div><Label>指示期間 終了</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
                <div><Label>受領日</Label><Input type="date" value={form.received_date} onChange={(e) => setForm({ ...form, received_date: e.target.value })} /></div>
              </div>
              <div><Label>傷病名</Label><Input value={form.diagnosis} onChange={(e) => setForm({ ...form, diagnosis: e.target.value })} /></div>
              <div><Label>指示内容</Label><Textarea value={form.content} rows={4} onChange={(e) => setForm({ ...form, content: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>キャンセル</Button>
              <Button data-testid="save-instruction-button" className="bg-[#2C5545] text-white" onClick={save}>追加</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {items.map((it) => (
          <Card key={it.id} className="p-4 border-[#E5E5E3]">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-[#2C5545]" />
                  <span className="font-medium">{INST_KINDS[it.kind]}</span>
                </div>
                <div className="text-sm text-[#4A4A48] mt-1 font-numeric">
                  {it.doctor_name && `${it.doctor_name} / `}{it.medical_institution}
                </div>
                <div className="text-xs text-[#8A8A86] font-numeric">
                  期間: {it.start_date || "-"} 〜 {it.end_date || "-"}
                </div>
                {it.diagnosis && <div className="text-sm mt-2">{it.diagnosis}</div>}
                {it.content && <div className="text-xs text-[#4A4A48] mt-1 whitespace-pre-wrap">{it.content}</div>}
                {it.image_base64 && <img src={it.image_base64} alt="" className="mt-2 max-h-40 rounded border border-[#E5E5E3]" />}
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <button onClick={() => del(it.id)} className="text-[#A74141]"><Trash2 size={14} /></button>
                <Button
                  data-testid={`create-instruction-request-${it.id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => createRequestDoc(it.id)}
                  className="border-[#2C5545] text-[#2C5545] whitespace-nowrap"
                >
                  <FileText size={13} className="mr-1" />依頼書作成
                </Button>
              </div>
            </div>
          </Card>
        ))}
        {items.length === 0 && <div className="text-center py-8 text-[#8A8A86]">指示書が登録されていません</div>}
      </div>
    </div>
  );
}
